import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} className="w-full h-11 mt-2 rounded-md bg-white text-[#1f1f1f] font-semibold text-sm inline-flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors" data-testid="google-login-button">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continue with Google
    </button>
  );
};

export default function AuthModal({ open, onOpenChange }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(loginEmail, loginPassword);
      toast.success(`Welcome back, ${u.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(regName, regEmail, regPassword);
      toast.success(`Account created. Welcome, ${u.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161f] border-white/10 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl uppercase tracking-tight">
            Join the <span className="cyan-text">Studio</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Save tabs, build your library, and sync across devices.
          </DialogDescription>
        </DialogHeader>

        <GoogleButton />
        <div className="flex items-center gap-3 text-[10px] font-tab uppercase tracking-widest text-slate-500">
          <span className="h-px flex-1 bg-white/10" /> or use email <span className="h-px flex-1 bg-white/10" />
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-white/5">
            <TabsTrigger value="login" data-testid="auth-tab-login">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" required value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@email.com" data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" required value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••" data-testid="login-password-input" />
              </div>
              {error && <p className="text-sm text-[#FF3366]" data-testid="auth-error">{error}</p>}
              <Button type="submit" disabled={loading}
                className="w-full bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold"
                data-testid="login-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input required value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder="Your name" data-testid="register-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" required value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="you@email.com" data-testid="register-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" required minLength={6} value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="min 6 characters" data-testid="register-password-input" />
              </div>
              {error && <p className="text-sm text-[#FF3366]" data-testid="auth-error">{error}</p>}
              <Button type="submit" disabled={loading}
                className="w-full bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold"
                data-testid="register-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
